const colors = {
  brand: [
    '#E6F2FF',
    '#B3DBFF',
    '#80C4FF',
    '#4DADFF',
    '#2196F3',
    '#0D47A1',
    '#0B3D91',
    '#082B81',
    '#061A70',
    '#041260',
  ] as const,
  secondary: [
    '#F0F4F8',
    '#D9E2EC',
    '#C1CCD6',
    '#A9B8C2',
    '#91A3B0',
    '#7B8E9E',
    '#6A7B8D',
    '#5A6B7C',
    '#4A5B6B',
    '#3A4B5A',
  ] as const,
  accent: [
    '#E6F4F2',
    '#B3E5DC',
    '#80D6C7',
    '#4DC7B2',
    '#26B99D',
    '#00A88B',
    '#00967D',
    '#00846E',
    '#007260',
    '#006052',
  ] as const,
  danger: [
    '#FFE8E8',
    '#FFD1D1',
    '#FFB9B9',
    '#FF9494',
    '#FF6B6B',
    '#FF4242',
    '#E63B3B',
    '#CC3434',
    '#B32E2E',
    '#992727',
  ] as const,
  cta: [
    '#FFE8E8',
    '#FFD1D1',
    '#FFB9B9',
    '#FF9494',
    '#FF6B6B',
    '#FF6B6B',
    '#FF6B6B',
    '#FF6B6B',
    '#FF6B6B',
    '#FF6B6B',
  ] as const,
  /* END OF CUSTOM COLORS */
  dark: [
    '#C9C9C9',
    '#b8b8b8',
    '#828282',
    '#696969',
    '#424242',
    '#3b3b3b',
    '#2e2e2e',
    '#242424',
    '#1f1f1f',
    '#141414',
  ],
  gray: [
    '#f8f9fa',
    '#f1f3f5',
    '#e9ecef',
    '#dee2e6',
    '#ced4da',
    '#adb5bd',
    '#868e96',
    '#495057',
    '#343a40',
    '#212529',
  ],
  red: [
    '#fff5f5',
    '#ffe3e3',
    '#ffc9c9',
    '#ffa8a8',
    '#ff8787',
    '#ff6b6b',
    '#fa5252',
    '#f03e3e',
    '#e03131',
    '#c92a2a',
  ],
  pink: [
    '#fff0f6',
    '#ffdeeb',
    '#fcc2d7',
    '#faa2c1',
    '#f783ac',
    '#f06595',
    '#e64980',
    '#d6336c',
    '#c2255c',
    '#a61e4d',
  ],
  grape: [
    '#f8f0fc',
    '#f3d9fa',
    '#eebefa',
    '#e599f7',
    '#da77f2',
    '#cc5de8',
    '#be4bdb',
    '#ae3ec9',
    '#9c36b5',
    '#862e9c',
  ],
  violet: [
    '#f3f0ff',
    '#e5dbff',
    '#d0bfff',
    '#b197fc',
    '#9775fa',
    '#845ef7',
    '#7950f2',
    '#7048e8',
    '#6741d9',
    '#5f3dc4',
  ],
  indigo: [
    '#edf2ff',
    '#dbe4ff',
    '#bac8ff',
    '#91a7ff',
    '#748ffc',
    '#5c7cfa',
    '#4c6ef5',
    '#4263eb',
    '#3b5bdb',
    '#364fc7',
  ],
  blue: [
    '#e7f5ff',
    '#d0ebff',
    '#a5d8ff',
    '#74c0fc',
    '#4dabf7',
    '#339af0',
    '#228be6',
    '#1c7ed6',
    '#1971c2',
    '#1864ab',
  ],
  cyan: [
    '#e3fafc',
    '#c5f6fa',
    '#99e9f2',
    '#66d9e8',
    '#3bc9db',
    '#22b8cf',
    '#15aabf',
    '#1098ad',
    '#0c8599',
    '#0b7285',
  ],
  teal: [
    '#e6fcf5',
    '#c3fae8',
    '#96f2d7',
    '#63e6be',
    '#38d9a9',
    '#20c997',
    '#12b886',
    '#0ca678',
    '#099268',
    '#087f5b',
  ],
  green: [
    '#ebfbee',
    '#d3f9d8',
    '#b2f2bb',
    '#8ce99a',
    '#69db7c',
    '#51cf66',
    '#40c057',
    '#37b24d',
    '#2f9e44',
    '#2b8a3e',
  ],
  lime: [
    '#f4fce3',
    '#e9fac8',
    '#d8f5a2',
    '#c0eb75',
    '#a9e34b',
    '#94d82d',
    '#82c91e',
    '#74b816',
    '#66a80f',
    '#5c940d',
  ],
  yellow: [
    '#fff9db',
    '#fff3bf',
    '#ffec99',
    '#ffe066',
    '#ffd43b',
    '#fcc419',
    '#fab005',
    '#f59f00',
    '#f08c00',
    '#e67700',
  ],
  orange: [
    '#fff4e6',
    '#ffe8cc',
    '#ffd8a8',
    '#ffc078',
    '#ffa94d',
    '#ff922b',
    '#fd7e14',
    '#f76707',
    '#e8590c',
    '#d9480f',
  ],
} as const;

type radius = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';

export const GLOBAL_STYLES = {
  borderRadius: 'md' as radius,
  avatarRadius: 'xl' as radius,
};

export const theme = {
  primaryColor: 'brand',
  colors: colors,
  black: '#000000',
  white: '#FFFFFF',
  fontSizes: {
    sm: '0.875rem',
    md: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.75rem',
    '3xl': '2.25rem',
  },
  fontFamily: 'Maven Pro, system-ui, sans-serif',
  headings: {
    fontFamily: 'Maven Pro, system-ui, sans-serif',
    fontWeight: '700',
    sizes: {
      h1: {
        fontSize: '2.5rem',
        lineHeight: '3rem',
      },
      h2: {
        fontSize: '2rem',
        lineHeight: '2.5rem',
      },
      h3: {
        fontSize: '1.5rem',
        lineHeight: '2rem',
      },
      h4: {
        fontSize: '1.25rem',
        lineHeight: '1.75rem',
      },
      h5: {
        fontSize: '1rem',
        lineHeight: '1.5rem',
      },
      h6: {
        fontSize: '0.875rem',
        lineHeight: '1.5rem',
      },
    },
  },
  lineHeights: {
    xs: '1.4',
    sm: '1.45',
    md: '1.55',
    lg: '1.6',
    xl: '1.65',
  },
  radius: {
    xs: '0.125rem',
    sm: '0.25rem',
    md: '0.5rem',
    lg: '1rem',
    xl: '2rem',
  },
  spacing: {
    xs: '0.625rem',
    sm: '0.75rem',
    md: '1rem',
    lg: '1.25rem',
    xl: '2rem',
  },
  shadows: {
    xs: '0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.05), 0 0.0625rem 0.125rem rgba(0, 0, 0, 0.1)',
    sm: '0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0 0.625rem 0.9375rem -0.3125rem, rgba(0, 0, 0, 0.04) 0 0.4375rem 0.4375rem -0.3125rem',
    md: '0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0 1.25rem 1.5625rem -0.3125rem, rgba(0, 0, 0, 0.04) 0 0.625rem 0.625rem -0.3125rem',
    lg: '0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0 1.75rem 1.4375rem -0.4375rem, rgba(0, 0, 0, 0.04) 0 0.75rem 0.75rem -0.4375rem',
    xl: '0 0.0625rem 0.1875rem rgba(0, 0, 0, 0.05), rgba(0, 0, 0, 0.05) 0 2.25rem 1.75rem -0.4375rem, rgba(0, 0, 0, 0.04) 0 1.0625rem 1.0625rem -0.4375rem',
  },
};
